import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { 
  Button, 
  Card, 
  Col, 
  Form, 
  Input, 
  Row, 
  Select, 
  Typography, 
  Divider, 
  Spin, 
  message, 
  Tooltip,
  Space
} from "antd";
import { 
  UserOutlined, 
  BankOutlined, 
  IdcardOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  GlobalOutlined, 
  LockOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  NumberOutlined
} from '@ant-design/icons';
import { addIndivisualUser } from "../../redux/rtk/features/user/userSlice";
import { addClient } from "../../redux/rtk/features/usercopy/clientSlice";
import { getAllMasterwithoutToken, getCitiesByStateId } from "../../redux/rtk/features/masters/masterSlice";
import Footer from "../layouts/Footer";
import Main from "../layouts/Main";
import "./registerClient.css";

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const RegisterClientOrStaff = () => {
  const [loader, setLoader] = useState(false);
  const [isOfficeRegister, setIsOfficeRegister] = useState(true);
  const [buttonClicked, setButtonClicked] = useState("office");
  const [stateId, setStateId] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const masterData = useSelector((state) => state?.masters?.mastersWt);
  const cities = useSelector((state) => state?.masters?.cities);
  const [isLoading, setIsLoading] = useState(true);

  // Sort functions for dropdown data
  const sortByName = (a, b, property) => {
    return a[property]?.localeCompare(b[property]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await dispatch(getAllMasterwithoutToken({ companyId: 0 }));
      setIsLoading(false);
    };
    
    fetchData();
    
    if (stateId) {
      dispatch(getCitiesByStateId(stateId));
    }
  }, [dispatch, stateId]);

  const handleStateChange = (value) => {
    setStateId(value);
    form.setFieldsValue({ CityId: undefined });
  };

  const onFinish = async (values) => {
    try {
      setLoader(true);
      
      if (isOfficeRegister) {
        const payload = {
          ...values,
          ClientId: 0,
          DataStatus: 1,
        };
        
        const resp = await dispatch(addClient(payload));
        
        if (resp.payload && resp.payload.message === "success") {
          message.success("Registration successful!");
          navigate("/admin/auth/login");
        } else {
          message.error("Registration failed. Please try again.");
          setLoader(false);
        }
      } else {
        const resp = await dispatch(addIndivisualUser(values));
        
        if (resp.payload && resp.payload.message === "success") {
          message.success("Registration successful!");
          navigate("/admin/auth/login");
        } else {
          message.error("Registration failed. Please try again.");
          setLoader(false);
        }
      }
      
      form.resetFields();
    } catch (error) {
      // console.log(error.message);
      message.error("An error occurred during registration");
      setLoader(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoader(false);
    message.error("Please check the form for errors");
    // console.log("Failed:", errorInfo);
  };

  // Filter function for searchable select
  const filterOption = (input, option) => {
    return (option?.children || '').toLowerCase().includes(input.toLowerCase());
  };

  if (isLoading) {
    return (
      <Main>
        <div className="loading-container">
          <Spin size="large" tip="Loading form data..." />
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <div className="registration-container">
        <Row justify="center">
          <Col xs={24} sm={22} md={20} lg={18} xl={16}>
            <Card 
              bordered={false} 
              className="registration-card"
              style={{ 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderRadius: "10px",
                overflow: "hidden"
              }}
            >
              <div className="registration-header">
                <Title level={2} className="registration-title">
                  Create Your Account
                </Title>
                <Text type="secondary" className="registration-subtitle">
                  Please fill in the details to get started
                </Text>
              </div>
              
              <div className="registration-tabs">
                <Button
                  type={buttonClicked === "office" ? "primary" : "default"}
                  onClick={() => {
                    setIsOfficeRegister(true);
                    setButtonClicked("office");
                    form.resetFields();
                  }}
                  className="tab-button"
                  icon={<BankOutlined />}
                  size="large"
                >
                  Office Registration
                </Button>
                <Button
                  type={buttonClicked === "user" ? "primary" : "default"}
                  onClick={() => {
                    setIsOfficeRegister(false);
                    setButtonClicked("user");
                    form.resetFields();
                  }}
                  className="tab-button"
                  icon={<UserOutlined />}
                  size="large"
                >
                  Individual Registration
                </Button>
              </div>
              
              <Divider />
              
              <Form
                form={form}
                layout="vertical"
                name="registration_form"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                scrollToFirstError
                requiredMark="optional"
                className="registration-form"
              >
                {isOfficeRegister ? (
                  <>
                    <div className="section-container">
                      <div className="section-heading">
                        <ShopOutlined /> Company Information
                      </div>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Company Name"
                            name="CompanyName"
                            rules={[{ required: true, message: "Please input company name!" }]}
                          >
                            <Input 
                              prefix={<BankOutlined className="site-form-item-icon" />} 
                              placeholder="Enter company name"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="GST Number"
                            name="GSTNumber"
                            
                          >
                            <Input 
                              prefix={<NumberOutlined className="site-form-item-icon" />} 
                              placeholder="Enter GST number" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Company Type"
                            name="CompanyTypeId"
                            rules={[{ required: true, message: "Please select a company type!" }]}
                          >
                            <Select 
                              placeholder="Select company type" 
                              showSearch 
                              optionFilterProp="children"
                              filterOption={filterOption}
                              allowClear
                            >
                              {[...(masterData?.CompanyTypeMaster || [])].sort((a, b) => 
                                sortByName(a, b, 'CompanyTypeName')
                              ).map((companyType) => (
                                <Option 
                                  key={companyType.CompanyTypeId} 
                                  value={companyType.CompanyTypeId}
                                >
                                  {companyType.CompanyTypeName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Contact Person Name"
                            name="ContactPersonName"
                            rules={[{ required: true, message: "Please input contact person name!" }]}
                          >
                            <Input 
                              prefix={<UserOutlined className="site-form-item-icon" />} 
                              placeholder="Enter contact person name" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                    
                    <div className="section-container">
                      <div className="section-heading">
                        <IdcardOutlined /> Contact Information
                      </div>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Email"
                            name="ContactPersonEmailId"
                            rules={[
                              { required: true, message: "Please input email!" },
                              { type: "email", message: "Please enter a valid email!" },
                            ]}
                          >
                            <Input 
                              prefix={<MailOutlined className="site-form-item-icon" />} 
                              placeholder="Enter email address" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Phone"
                            name="ContactPersonNumber"
                            rules={[
                              { required: true, message: "Please input phone!" },
                              {
                                pattern: /^[0-9]{10}$/,
                                message: "Please enter a valid 10-digit phone number!",
                              },
                            ]}
                          >
                            <Input 
                              prefix={<PhoneOutlined className="site-form-item-icon" />} 
                              placeholder="Enter phone number" 
                              maxLength={10} 
                              addonBefore="+91" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Alternate Phone"
                            name="ContactPersonAlternateNumber"
                          >
                            <Input 
                              prefix={<PhoneOutlined className="site-form-item-icon" />} 
                              placeholder="Enter alternate phone number" 
                              maxLength={10} 
                              addonBefore="+91" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Address"
                            name="StreetAddress"
                            rules={[{ required: true, message: "Please input address!" }]}
                          >
                            <TextArea 
                              placeholder="Enter street address"
                              autoSize={{ minRows: 2, maxRows: 4 }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                    
                    <div className="section-container">
                      <div className="section-heading">
                        <GlobalOutlined /> Location Details
                      </div>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Country"
                            name="CountryId"
                            rules={[{ required: true, message: "Please select a country!" }]}
                          >
                            <Select 
                              placeholder="Select country" 
                              showSearch 
                              optionFilterProp="children"
                              filterOption={filterOption}
                              allowClear
                            >
                              {[...(masterData?.CountriesMaster || [])].sort((a, b) => 
                                sortByName(a, b, 'CountryName')
                              ).map((country) => (
                                <Option key={country.CountryId} value={country.CountryId}>
                                  {country.CountryName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="State"
                            name="StateId"
                            rules={[{ required: true, message: "Please select a state!" }]}
                          >
                            <Select
                              placeholder="Select state"
                              showSearch
                              optionFilterProp="children"
                              filterOption={filterOption}
                              allowClear
                              onChange={(value) => handleStateChange(value)}
                            >
                              {[...(masterData?.StatesMaster || [])].sort((a, b) => 
                                sortByName(a, b, 'StateName')
                              ).map((state) => (
                                <Option key={state.StateId} value={state.StateId}>
                                  {state.StateName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="City"
                            name="CityId"
                            rules={[{ required: true, message: "Please select a city!" }]}
                          >
                            <Select
                              placeholder="Select city"
                              showSearch
                              optionFilterProp="children"
                              filterOption={filterOption}
                              allowClear
                              disabled={!form.getFieldValue("StateId")}
                              notFoundContent={form.getFieldValue("StateId") 
                                ? "No cities found" 
                                : "Please select a state first"}
                            >
                              {[...(cities?.filter((city) => 
                                city?.StateId === form.getFieldValue("StateId")
                              ) || [])].sort((a, b) => 
                                sortByName(a, b, 'CityName')
                              )?.map((city) => (
                                <Option key={city.CityId} value={city.CityId}>
                                  {city.CityName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Zip Code"
                            name="ZipCode"
                            rules={[
                              { required: true, message: "Please input zip code!" },
                              {
                                pattern: /^[0-9]{6}$/,
                                message: "Please enter a valid 6-digit Zip Code!",
                              },
                            ]}
                          >
                            <Input 
                              prefix={<HomeOutlined className="site-form-item-icon" />} 
                              placeholder="Enter zip code" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                    
                    <div className="section-container">
                      <div className="section-heading">
                        <LockOutlined /> Account Information
                      </div>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Username"
                            name="UserName"
                            rules={[
                              { required: true, message: "Please input username!" },
                              { min: 4, message: "Username must be at least 4 characters!" }
                            ]}
                          >
                            <Input 
                              prefix={<UserOutlined className="site-form-item-icon" />} 
                              placeholder="Create username" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Password"
                            name="UserPassword"
                            rules={[
                              { required: true, message: "Please input password!" },
                              { min: 6, message: "Password must be at least 6 characters!" },
                            ]}
                          >
                            <Input.Password 
                              prefix={<LockOutlined className="site-form-item-icon" />} 
                              placeholder="Create password" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="section-container">
                      <div className="section-heading">
                        <UserOutlined /> Personal Information
                      </div>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="First Name"
                            name="firstName"
                            rules={[{ required: true, message: "Please input first name!" }]}
                          >
                            <Input 
                              prefix={<UserOutlined className="site-form-item-icon" />} 
                              placeholder="Enter first name" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Last Name"
                            name="lastName"
                            rules={[{ required: true, message: "Please input last name!" }]}
                          >
                            <Input 
                              prefix={<UserOutlined className="site-form-item-icon" />} 
                              placeholder="Enter last name" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      {/* Add more individual registration fields as needed */}
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                              { required: true, message: "Please input email!" },
                              { type: "email", message: "Please enter a valid email!" },
                            ]}
                          >
                            <Input 
                              prefix={<MailOutlined className="site-form-item-icon" />} 
                              placeholder="Enter email address" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Phone"
                            name="phoneNumber"
                            rules={[
                              { required: true, message: "Please input phone!" },
                              {
                                pattern: /^[0-9]{10}$/,
                                message: "Please enter a valid 10-digit phone number!",
                              },
                            ]}
                          >
                            <Input 
                              prefix={<PhoneOutlined className="site-form-item-icon" />} 
                              placeholder="Enter phone number" 
                              maxLength={10} 
                              addonBefore="+91" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Row gutter={[24, 0]}>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Username"
                            name="userName"
                            rules={[
                              { required: true, message: "Please input username!" },
                              { min: 4, message: "Username must be at least 4 characters!" }
                            ]}
                          >
                            <Input 
                              prefix={<UserOutlined className="site-form-item-icon" />} 
                              placeholder="Create username" 
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                          <Form.Item
                            label="Password"
                            name="password"
                            rules={[
                              { required: true, message: "Please input password!" },
                              { min: 6, message: "Password must be at least 6 characters!" },
                            ]}
                          >
                            <Input.Password 
                              prefix={<LockOutlined className="site-form-item-icon" />} 
                              placeholder="Create password" 
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      shape="round"
                      size="large"
                      loading={loader}
                      icon={<CheckCircleOutlined />}
                      className="submit-button"
                    >
                      Register Now
                    </Button>
                  </Form.Item>
                  
                  <div className="login-link">
                    <Text>Already have an account?</Text>
                    <Link to="/admin/auth/login">Login Here</Link>
                  </div>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
      <Footer />
    </Main>
  );
};

export default RegisterClientOrStaff;